import { useEffect } from "react";
import { useIntegrationsStore } from "../stores/integrationsStore";

/**
 * Hook for managing integrations.
 * Provides easy access to integration state and actions.
 */
export function useIntegrations(provider?: string) {
  const store = useIntegrationsStore();

  // Fetch status on mount if provider is specified
  useEffect(() => {
    if (provider) {
      store.fetchStatus(provider);
    }
  }, [provider]);

  const integration = provider ? store.integrations[provider] : null;

  // Status is loading if we haven't fetched it yet OR if we're currently fetching
  const statusLoading = provider ? (integration === undefined || integration?.isLoading) : false;

  return {
    // State
    integration,
    status: integration?.status ?? "disconnected",
    isConnected: integration?.status === "connected",
    isLoading: integration?.isLoading ?? false,
    statusLoading,
    errorMessage: integration?.errorMessage ?? null,
    lastSyncAt: integration?.lastSyncAt ?? null,
    needsReconnect: integration?.needsReconnect ?? false,

    // Activities
    activities: store.activities,
    activitiesLoading: store.activitiesLoading,
    activitiesError: store.activitiesError,

    // Modal state
    connectModalOpen: store.connectModalOpen,
    connectModalProvider: store.connectModalProvider,

    // Actions
    fetchStatus: store.fetchStatus,
    connect: store.connect,
    disconnect: store.disconnect,
    fetchActivities: store.fetchActivities,
    openConnectModal: store.openConnectModal,
    closeConnectModal: store.closeConnectModal,
  };
}

/**
 * Hook for Garmin-specific integration.
 * Convenience wrapper around useIntegrations.
 */
export function useGarminIntegration() {
  return useIntegrations("garmin");
}

/**
 * Hook for Strava-specific integration.
 * Convenience wrapper around useIntegrations.
 */
export function useStravaIntegration() {
  return useIntegrations("strava");
}
